import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import Logo from '../Assets/nav-logo.png';
import { useTaxPortal } from '../utils/useTaxPortal';
import { getApiUrl, isAdminRole } from '../authConfig';
import { getValidToken } from '../utils/msalTokenUtil';
import TaxPortalLayout from '../components/Common/TaxPortalLayout';
import AddressAutocomplete from '../components/Common/AddressAutocomplete';
import {
  FaMoneyBillWave, FaClock, FaBuilding, FaEye, FaEnvelope, FaPhoneAlt, FaTrashAlt,
  FaPlus, FaPrint, FaArrowLeft, FaUser, FaFileExcel, FaSave,
  FaChevronLeft, FaChevronRight, FaEllipsisV, FaInbox,
  FaFileInvoiceDollar, FaSearch, FaFilter, FaCheckCircle, FaBan, FaList
} from 'react-icons/fa';

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 5 }, (_, i) => (currentYear - i).toString());

const WORK_ITEMS = {
  Personal: [
    "Basic Personal Tax Return (T1): Standard filing for individuals with employment income (T4s), basic deductions, and standard credits.",
    "Self-Employment / Sole Proprietorship (T2125): Preparation of business or professional income and expense statements.",
    "Rental Income Schedule (T776): Preparation of statements for real estate rental income and applicable deductions.",
    "GST/HST New Housing Rebate: Application for rebates on new or substantially renovated housing for personal use.",
    "Prior Year Adjustments (T1-ADJ): Amending previously filed personal tax returns.",
    "CRA Audit Support (Personal): Responding to CRA pre-assessment reviews, processing reviews, or full audits on behalf of the individual."
  ],
  Corporate: [
    "Corporate Tax Return (T2): Standard preparation and filing of the annual corporate tax return.",
    "GST/HST Return Preparation: Calculating and filing periodic sales tax returns (monthly, quarterly, or annually).",
    "GST/HST Rebate Application: Filing for rebates applicable to businesses, such as public service bodies' rebates.",
    "Full-Service Payroll Processing: Comprehensive management of payroll cycles, including wage calculation, statutory deductions and pay stub generation.",
    "Information Slips Preparation (T4/T4A/T5): Calculating and filing payroll or dividend summaries and generating slips for employees and shareholders.",
    "Bookkeeping Services: Monthly, quarterly, or annual data entry, bank reconciliations, and general ledger maintenance.",
    "Incorporation Registration: Federal or Provincial incorporation of a new business entity, including preparation of articles of incorporation.",
    "Corporate Name Search & Registration (NUANS): Conducting a mandatory name search and reserving a corporate name for incorporation.",
    "WSIB / EHT Filings: Preparation of Workplace Safety and Insurance Board or Employer Health Tax returns.",
    "CRA Audit Support (Corporate): Representing the corporation during a CRA payroll, GST/HST, or corporate income tax audit."
  ]
};

// Avatar helper
const AVATAR_COLORS = [
  { bg: '#dbeafe', text: '#1e40af' }, { bg: '#fce7f3', text: '#9d174d' },
  { bg: '#d1fae5', text: '#065f46' }, { bg: '#ede9fe', text: '#5b21b6' },
  { bg: '#fef3c7', text: '#92400e' }, { bg: '#cffafe', text: '#164e63' },
  { bg: '#fee2e2', text: '#991b1b' }, { bg: '#e0f2fe', text: '#0c4a6e' },
];
const getAvatarColor = (name = '') => {
  const code = (name.charCodeAt(0) || 0) + (name.charCodeAt(1) || 0);
  return AVATAR_COLORS[code % AVATAR_COLORS.length];
};
const getInitials = (name = '') => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (parts[0] || 'I').substring(0, 2).toUpperCase();
};

const ClientAvatar = ({ name }) => {
  const c = getAvatarColor(name);
  return (
    <span className="name-avatar flex-shrink-0 d-none d-sm-flex" style={{ width: 32, height: 32, backgroundColor: c.bg, color: c.text, fontSize: '11px' }} aria-hidden="true">
      {getInitials(name)}
    </span>
  );
};

const SkeletonRows = ({ count = 7 }) => (
  <>
    {Array.from({ length: count }).map((_, i) => (
      <tr key={i} className="skeleton-row">
        <td className="px-3"><span className="skeleton-cell d-block" style={{ width: 60, height: 16 }} /></td>
        <td><span className="skeleton-cell d-block" style={{ width: 80, height: 16 }} /></td>
        <td>
          <div className="d-flex align-items-center gap-2">
            <span className="skeleton-cell d-none d-sm-block" style={{ width: 32, height: 32, borderRadius: '50%' }} />
            <span className="skeleton-cell d-block" style={{ width: 120 + (i % 3) * 30, height: 16 }} />
          </div>
        </td>
        <td><span className="skeleton-cell d-block" style={{ width: 60, height: 16 }} /></td>
        <td><span className="skeleton-cell d-block" style={{ width: 60, height: 16 }} /></td>
        <td><span className="skeleton-cell d-block" style={{ width: 60, height: 16 }} /></td>
        <td><span className="skeleton-cell d-block" style={{ width: 60, height: 16 }} /></td>
        <td><span className="skeleton-cell d-block" style={{ width: 80, height: 16 }} /></td>
        <td><span className="skeleton-cell d-block" style={{ width: 90, height: 26, borderRadius: 99 }} /></td>
        <td className="px-4"><span className="skeleton-cell d-block ms-auto" style={{ width: 60, height: 26, borderRadius: 99 }} /></td>
      </tr>
    ))}
  </>
);

const InvoicePage = () => {
  const portalState = useTaxPortal();
  const location = useLocation();
  const { account, msalInstance, isInitialized } = portalState;

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [invoiceId, setInvoiceId] = useState(null);
  const [savedInvoices, setSavedInvoices] = useState([]);
  const [invoiceNum, setInvoiceNum] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [billToName, setBillToName] = useState('');
  const [billToEmail, setBillToEmail] = useState('');
  const [billToAddress, setBillToAddress] = useState('');
  const [sourceRecord, setSourceRecord] = useState(null);
  const [shouldTriggerPrint, setShouldTriggerPrint] = useState(false);
  const [pendingEmailAction, setPendingEmailAction] = useState(false);

  const [viewMode, setViewMode] = useState(() => {
    const state = location.state;
    if (state && state.customer) return 'form';
    return 'dashboard';
  });

  const [items, setItems] = useState(() => {
    const state = location.state;
    if (state && state.customer) {
      const defaultDesc = state.taxType === 'Corporate' ? WORK_ITEMS.Corporate[0] : WORK_ITEMS.Personal[0];
      const parts = defaultDesc.split(':');
      return [{
        id: Date.now(),
        itemName: parts[0].trim(),
        itemDesc: parts.slice(1).join(':').trim(),
        period: "yearly",
        startDate: "",
        endDate: "",
        amount: "",
        qty: 1,
        hst: true
      }];
    }
    return [{ id: Date.now(), itemName: "", itemDesc: "", period: "monthly", startDate: "", endDate: "", amount: "", qty: 1, hst: true }];
  });

  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('Due');
  const [yearFilter, setYearFilter] = useState(currentYear.toString());
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterRef = useRef(null);
  const [pageIndex, setPageIndex] = useState(0);
  const pageSize = 10;
  const originalTitleRef = useRef(document.title);

  useEffect(() => {
    document.body.classList.add('invoice-body');
    // Cleanup function to remove the class when the component unmounts
    return () => {
      document.body.classList.remove('invoice-body');
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setIsFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isAdmin = isAdminRole(account?.username);

  useEffect(() => {
    if (account && !isAdmin && viewMode === 'dashboard') {
      setViewMode('form');
    }
  }, [account, isAdmin, viewMode]);

  const { paidTotal, dueTotal, paidTaxTotal, personalTotal, corporateTotal, paidCount, dueCount, cancelledCount, totalCount } = useMemo(() => {
    let paidTotal = 0;
    let dueTotal = 0;
    let paidTaxTotal = 0;
    let personalTotal = 0;
    let corporateTotal = 0;
    let paidCount = 0;
    let dueCount = 0;
    let cancelledCount = 0;
    let totalCount = 0;

    savedInvoices.forEach(inv => {
      const total = parseFloat(inv.total) || 0;
      const tax = parseFloat(inv.tax) || 0;
      const status = inv.status || 'Due';

      if (status === 'Paid') {
        paidTotal += total;
        paidTaxTotal += tax;
        paidCount += 1;
      } else if (status === 'Due') {
        dueTotal += total;
        dueCount += 1;
      } else if (status === 'Cancelled') {
        cancelledCount += 1;
      }
      totalCount += 1;

      let parsedItems = [];
      if (typeof inv.items === 'string') {
        try { parsedItems = JSON.parse(inv.items); } catch(e){}
      } else if (Array.isArray(inv.items)) {
        parsedItems = inv.items;
      }

      parsedItems.forEach(item => {
        const amt = parseFloat(item.amount?.toString().replace(/[^0-9.-]/g, '')) || 0;
        const itemName = item.itemName || (item.description ? item.description.split(':')[0].trim() : '');
        
        const nameLower = itemName.toLowerCase();
        const personalNames = WORK_ITEMS.Personal.map(wi => wi.split(':')[0].trim().toLowerCase());
        const isPersonal = nameLower.includes('personal') || personalNames.includes(nameLower);

        if (isPersonal) {
          personalTotal += amt;
        } else {
          corporateTotal += amt;
        }
      });
    });

    return { paidTotal, dueTotal, paidTaxTotal, personalTotal, corporateTotal, paidCount, dueCount, cancelledCount, totalCount };
  }, [savedInvoices]);

  useEffect(() => {
    const today = new Date();
    const due = new Date(today);
    due.setDate(today.getDate() + 2);
    const formatDate = date => date.toISOString().slice(0, 10);

    if (!invoiceId) {
      setInvoiceDate(formatDate(today));
      setDueDate(formatDate(due));
      
      if (location.state && location.state.customer) {
        const cust = location.state.customer;
        setBillToName(cust.name || cust.businessName || '');
        setBillToEmail(cust.email || '');
        setBillToAddress(cust.address || '');
        setSourceRecord({ id: cust.id || cust._id, type: location.state.taxType });
      }
    }

    const fetchNextInvoiceNumber = async () => {
      if (!account || !isInitialized) return;
      try {
        const token = await getValidToken(msalInstance, account);
        const response = await fetch(getApiUrl('/v1/invoices/next-number'), { headers: { Authorization: `Bearer ${token}` } });
        if (response.ok) {
          const data = await response.json();
          setInvoiceNum(data.invoice_num);
        }
      } catch (err) { console.error(err); toast.error("Failed to fetch next invoice number."); }
    };

    const fetchSavedInvoices = async () => {
      if (!account || !isInitialized) return;
      setIsLoading(true);
      try {
        const token = await getValidToken(msalInstance, account);
        const response = await fetch(getApiUrl('/v1/invoices'), { headers: { Authorization: `Bearer ${token}` } });
        if (response.ok) {
          setSavedInvoices(await response.json());
        }
      } catch (err) { console.error(err); toast.error("Failed to fetch saved invoices."); }
      finally { setIsLoading(false); }
    };

    if (!invoiceId && viewMode === 'form') fetchNextInvoiceNumber();
    if (viewMode === 'dashboard') fetchSavedInvoices();
  }, [account, isInitialized, msalInstance, invoiceId, location.state, viewMode, setViewMode]);

  const { subtotal, taxableSubtotal, tax, total } = useMemo(() => {
    const currentSubtotal = items.reduce((sum, item) => {
      const val = parseFloat(item.amount?.toString().replace(/[^0-9.]/g, '')) || 0;
      const qty = parseFloat(item.qty) || 1;
      return sum + (val * qty);
    }, 0);
    const taxableAmt = items.reduce((sum, item) => {
      const val = parseFloat(item.amount?.toString().replace(/[^0-9.]/g, '')) || 0;
      const qty = parseFloat(item.qty) || 1;
      return item.hst ? sum + (val * qty) : sum;
    }, 0);
    const currentTax = taxableAmt * 0.13;
    return { subtotal: currentSubtotal, taxableSubtotal: taxableAmt, tax: currentTax, total: currentSubtotal + currentTax };
  }, [items]);

  useEffect(() => {
    if (shouldTriggerPrint) {
      setShouldTriggerPrint(false);
      window.print();
      if (pendingEmailAction) {
        setPendingEmailAction(false);
        portalState.setEmailModalConfig({
          customer: { email: billToEmail, name: billToName },
          action: 'sendInvoice',
          taxType: sourceRecord ? sourceRecord.type : 'Personal',
          customData: {
            invoiceNum,
            dueDate,
            totalAmount: total.toFixed(2)
          }
        });
      }
    }
  }, [shouldTriggerPrint, pendingEmailAction, portalState, billToEmail, billToName, invoiceNum, dueDate, total, sourceRecord]);

  useEffect(() => {
    const textareas = document.querySelectorAll('.invoice-box textarea');
    textareas.forEach(ta => {
      ta.style.height = 'auto';
      ta.style.height = `${ta.scrollHeight}px`;
    });
  }, [items, viewMode]);

  useEffect(() => {
    const handleBeforePrint = () => {
      if (viewMode === 'form') {
        const safeBillTo = billToName ? billToName.replace(/[\s/\\:*?"<>|]/g, '_') : 'Client';
        document.title = `invoice_#${invoiceNum}_${safeBillTo}_${invoiceDate}`;
      }
    };
    const handleAfterPrint = () => { document.title = originalTitleRef.current; };

    window.addEventListener('beforeprint', handleBeforePrint);
    window.addEventListener('afterprint', handleAfterPrint);
    return () => {
      window.removeEventListener('beforeprint', handleBeforePrint);
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [invoiceNum, billToName, invoiceDate, viewMode]);

  const handleAddRow = () => {
    setItems(prev => [...prev, { id: Date.now(), itemName: "", itemDesc: "", period: "yearly", startDate: "", endDate: "", amount: "", qty: 1, hst: true }]);
  };

  const handleRemoveRow = (id) => {
    if (items.length > 1) {
      setItems(prev => prev.filter(item => item.id !== id));
    }
  };

  const handleItemChange = (id, field, value) => {
    setItems(prev => prev.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  const handleTextareaInput = (e) => {
    const element = e.target;
    element.style.height = 'auto';
    element.style.height = `${element.scrollHeight}px`;
  };

  const handleStatusChange = async (invoice, newStatus) => {
    try {
      const token = await getValidToken(msalInstance, account);
      
      const updatePayload = { ...invoice, status: newStatus };

      const response = await fetch(getApiUrl(`/v1/invoices/${invoice.id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(updatePayload)
      });

      if (response.ok) {
        setSavedInvoices(prev => prev.map(inv => inv.id === invoice.id ? { ...inv, status: newStatus } : inv));
        
        if (newStatus !== 'Cancelled') {
           await fetch(getApiUrl(`/v1/invoices/${invoice.id}/sync-status`), {
             method: 'POST',
             headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
             body: JSON.stringify({ status: newStatus, invoice_num: invoice.invoice_num })
           });
        }
      } else {
        toast.error('Failed to update invoice status.');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error updating invoice status.');
    }
  };

  const handleViewInvoice = (inv) => {
    setInvoiceId(inv.id);
    setInvoiceNum(inv.invoice_num);
    setInvoiceDate(inv.invoice_date);
    setDueDate(inv.due_date);
    try {
      const parsedBillTo = JSON.parse(inv.bill_to_id);
      setBillToName(parsedBillTo.name || '');
      setBillToEmail(parsedBillTo.email || '');
      setBillToAddress(parsedBillTo.address || '');
    } catch (e) {
      setBillToName(inv.bill_to_id || '');
      setBillToEmail('');
      setBillToAddress('');
    }
    let loadedItems = typeof inv.items === 'string' ? JSON.parse(inv.items) : inv.items;
    loadedItems = loadedItems.map(item => {
      const name = item.itemName !== undefined ? item.itemName : (item.description ? item.description.split(':')[0].trim() : '');
      const desc = item.itemDesc !== undefined ? item.itemDesc : (item.description && item.description.includes(':') ? item.description.split(':').slice(1).join(':').trim() : '');
      return { ...item, itemName: name, itemDesc: desc };
    });
    setItems(loadedItems);
    setViewMode('form');
  };

  const handleEmailInvoice = (inv) => {
    let targetEmail = '';
    let targetName = '';
    try {
      const parsed = JSON.parse(inv.bill_to_id);
      targetEmail = parsed.email || '';
      targetName = parsed.name || '';
      // Address is not used here but good practice to be aware of it.
    } catch (e) {}
    portalState.setEmailModalConfig({
      customer: { email: targetEmail, name: targetName },
      action: 'sendInvoice',
      taxType: 'Personal',
      customData: {
        invoiceNum: inv.invoice_num,
        dueDate: inv.due_date,
        totalAmount: (inv.total || 0).toFixed(2)
      }
    });
  };

  const handleSaveInvoice = async (shouldPrint = false, shouldEmail = false) => {
    if (!account) {
      toast.error('You must be logged in to save invoices.');
      return;
    }
    
    setIsSaving(true);
    try {
      const token = await getValidToken(msalInstance, account);
      const payload = {
        invoice_num: invoiceNum,
        invoice_date: invoiceDate,
        due_date: dueDate,
        bill_to_id: JSON.stringify({ name: billToName, email: billToEmail, address: billToAddress }),
        items: items.map(item => ({
          ...item,
          description: item.itemDesc ? `${item.itemName}: ${item.itemDesc}` : (item.itemName || '')
        })),
        subtotal,
        tax,
        total,
        status: invoiceId ? (savedInvoices.find(i => i.id === invoiceId)?.status || 'Due') : 'Due'
      };

      let url = getApiUrl('/v1/invoices');
      let method = 'POST';

      if (invoiceId) {
        url = getApiUrl(`/v1/invoices/${invoiceId}`);
        method = 'PUT';
      }

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        const newInvoiceId = invoiceId || data.id;
        const newInvoiceNum = data.invoice_num || invoiceNum;
        
        if (!invoiceId && data.id) {
          setInvoiceId(newInvoiceId);
          setInvoiceNum(newInvoiceNum);
        }

        if (sourceRecord) {
          try {
            const isCorp = sourceRecord.type === 'Corporate';
            const updateUrl = getApiUrl(isCorp ? `/v1/corporate/${sourceRecord.id}` : `/v1/customers/${sourceRecord.id}`);
            const updatePayload = {
              invoiceNo: newInvoiceNum.toString(),
              invoiceAmount: total.toFixed(2),
              status: 'Payment Pending',
              updatedBy: account?.username || account?.name || 'System'
            };
            
            if (isCorp) {
              updatePayload.paymentStatus = 'Unpaid';
              updatePayload.invoiceStatus = 'Sent';
            } else {
              updatePayload.paymentReceived = 'Pending';
            }
            
            await fetch(updateUrl, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify(updatePayload)
            });
          } catch (err) { console.error(err); toast.error("Failed to sync invoice status to the source record."); }
        }
        
        toast.success('Invoice saved successfully!');
        if (shouldEmail) setPendingEmailAction(true);
        if (shouldPrint) setShouldTriggerPrint(true);
        
        const res = await fetch(getApiUrl('/v1/invoices'), { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) setSavedInvoices(await res.json());
      } else {
        toast.error('Failed to save invoice.');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error saving invoice.');
    } finally {
      setIsSaving(false);
    }
  };

  const filteredInvoices = savedInvoices.filter(inv => {
    const status = inv.status || 'Due';
    if (statusFilter !== 'All' && status !== statusFilter) {
      return false;
    }

    if (yearFilter && inv.invoice_date) {
      const invoiceYear = new Date(inv.invoice_date).getFullYear().toString();
      if (invoiceYear !== yearFilter) {
        return false;
      }
    }
    const searchLower = searchText.toLowerCase();
    let billTo = 'Unknown';
    try {
      const parsed = JSON.parse(inv.bill_to_id);
      billTo = (parsed.name || inv.bill_to_id).toLowerCase();
      // Note: address is not part of search, which is probably fine.
    } catch (e) { billTo = String(inv.bill_to_id || '').toLowerCase(); }
    
    let service = 'Unknown';
    try {
      const parsedItems = typeof inv.items === 'string' ? JSON.parse(inv.items) : inv.items;
      if (parsedItems && parsedItems.length > 0) {
        service = (parsedItems[0].itemName || parsedItems[0].description?.split(':')[0] || 'Service').toLowerCase();
      }
    } catch (e) {}

    return String(inv.invoice_num).includes(searchLower) ||
           billTo.includes(searchLower) ||
           service.includes(searchLower) ||
           String(inv.invoice_date).includes(searchLower) ||
           String((inv.total || 0).toFixed(2)).includes(searchLower);
  });

  const handleExportToExcel = async () => {
    const XLSX = await import('xlsx');
    
    const exportData = filteredInvoices.map((inv) => {
      let billTo = 'Unknown';
      try {
        const parsed = JSON.parse(inv.bill_to_id);
        billTo = parsed.name || inv.bill_to_id || '';
      } catch (e) { billTo = inv.bill_to_id; }
      
      let parsedItems = [];
      if (typeof inv.items === 'string') {
        try { parsedItems = JSON.parse(inv.items); } catch(e){}
      } else if (Array.isArray(inv.items)) {
        parsedItems = inv.items;
      }

      let personalAmt = 0;
      let corporateAmt = 0;

      parsedItems.forEach(item => {
        const amt = parseFloat(item.amount?.toString().replace(/[^0-9.-]/g, '')) || 0;
        const itemName = item.itemName || (item.description ? item.description.split(':')[0].trim() : '');
        const nameLower = itemName.toLowerCase();
        const personalNames = WORK_ITEMS.Personal.map(wi => wi.split(':')[0].trim().toLowerCase());
        const isPersonal = nameLower.includes('personal') || personalNames.includes(nameLower);

        if (isPersonal) personalAmt += amt;
        else corporateAmt += amt;
      });

      return {
        'Invoice #': inv.invoice_num,
        'Date': inv.invoice_date,
        'Due Date': inv.due_date,
        'Bill To': billTo,
        'Personal Amount': personalAmt.toFixed(2),
        'Corporate Amount': corporateAmt.toFixed(2),
        'Subtotal': (inv.subtotal || 0).toFixed(2),
        'Tax': (inv.tax || 0).toFixed(2),
        'Total': (inv.total || 0).toFixed(2),
        'Status': inv.status || 'Due'
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Invoices');
    const timestamp = new Date().toISOString().replace(/T/, '_').replace(/:/g, '-').split('.')[0];
    XLSX.writeFile(workbook, `Invoices_Report_${timestamp}.xlsx`);
  };

  const totalPages = Math.max(1, Math.ceil(filteredInvoices.length / pageSize));
  const pageData = filteredInvoices.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);

  return (
    <TaxPortalLayout
      title="Invoice Generator"
      description="Create, view, manage, and print client invoices."
      portalState={portalState}
    >
      
      <style>{`
        :root {
          --primary: #2563eb;
          --primary-dark: #1d4ed8;
          --text-dark: #0f172a;
          --text-muted: #64748b;
          --border-color: #e2e8f0;
          --bg-light: #f8fafc;
        }
        .invoice-box {
          position: relative;
          background: #ffffff;
          padding: 44px 48px;
          border-radius: 14px;
          margin: auto;
          box-sizing: border-box;
          box-shadow: 0 14px 35px -5px rgba(0, 0, 0, 0.06), 0 4px 6px -2px rgba(0, 0, 0, 0.02);
          border: 1px solid #e2e8f0;
          overflow: hidden;
          transition: all 0.2s ease;
        }
        .invoice-box::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 6px;
          background: linear-gradient(90deg, #2563eb 0%, #3b82f6 60%, #c5221f 100%);
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .header-grid {
          display: flex;
          flex-wrap: wrap;
          justify-content: space-between;
          align-items: flex-start;
          gap: 24px;
          border-bottom: 1.5px solid var(--border-color);
          padding-bottom: 28px;
          margin-bottom: 28px;
        }
        .header-company-info { flex: 2; min-width: 280px; }
        .logo-container { flex: 1; min-width: 220px; display: flex; justify-content: flex-end; align-items: center; padding-left: 20px; overflow: visible; }
        .logo-container img.logo { max-width: 250px; height: auto; object-fit: contain; display: block; margin-left: auto; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.03)); }
        .invoice-title {
          font-size: 36px;
          font-weight: 800;
          color: #c5221f;
          text-transform: uppercase;
          letter-spacing: 2px;
          margin: 0;
          line-height: 1.1;
        }
        .shaded-details-container {
          background-color: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          padding: 24px 28px;
          margin-bottom: 28px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .bill-meta-grid {
          display: flex;
          flex-wrap: wrap;
          justify-content: space-between;
          align-items: flex-start;
          gap: 28px;
        }
        .billing-details {
          flex: 1.2;
          min-width: 240px;
        }
        .billing-address-box .form-control {
          border: 1px solid #cbd5e1 !important;
          border-radius: 6px !important;
          padding: 6px 12px !important;
          font-size: 13px !important;
          background-color: #ffffff !important;
          color: #0f172a !important;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
        }
        .billing-address-box .form-control:focus {
          border-color: var(--primary) !important;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12) !important;
        }
        .invoice-meta-section {
          flex: 1;
          min-width: 240px;
          display: flex;
          flex-direction: column;
          border-left: 3px solid var(--primary);
          padding-left: 20px;
        }
        .section-subtitle {
          font-size: 12px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.7px;
          color: #475569;
          margin-bottom: 10px;
        }
        .meta-grid {
          display: flex;
          flex-direction: column;
          gap: 8px;
          width: 100%;
          max-width: 290px;
        }
        .meta-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-size: 13px;
          color: var(--text-dark);
        }
        .meta-label {
          color: #475569;
          font-weight: 600;
          font-size: 13px;
          width: 105px;
        }
        .meta-input {
          border: none;
          background: transparent;
          font-family: inherit;
          font-size: 13px;
          font-weight: 600;
          color: #0f172a;
          padding: 2px 4px;
          width: 140px;
          text-align: right;
          box-shadow: none;
        }
        .meta-input:focus {
          outline: 1px solid var(--primary);
          border-radius: 4px;
          background: #ffffff;
        }
        .meta-value {
          font-size: 13px;
          color: #0f172a;
          font-weight: 600;
          padding: 4px 8px;
        }
        .section-title {
          font-size: 15px;
          font-weight: 700;
          color: #0f172a;
          border-bottom: 1.5px solid var(--border-color);
          padding-bottom: 10px;
          margin-bottom: 16px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        select {
          font-family: inherit; padding: 6px 12px; border: 1px solid var(--border-color);
          border-radius: 6px; background-color: #fff; width: 100%; max-width: 320px; cursor: pointer; color: var(--text-dark);
        }
        .period-container { display: flex; flex-direction: column; align-items: flex-start; gap: 6px; }
        td select { padding: 4px 8px; width: 100%; max-width: 120px; font-size: 13px; }
        .date-range { display: flex; align-items: center; gap: 6px; font-size: 13px; color: var(--text-muted); }
        .date-range input[type="date"] {
          font-family: inherit; border: 1px solid var(--border-color); border-radius: 6px;
          padding: 3px 8px; font-size: 12px; color: var(--text-dark);
        }
        .billing-address-box { margin-top: 4px; font-size: 14px; line-height: 1.5; color: var(--text-muted); }
        .print-address-view { display: none; }
        table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
          margin-top: 18px;
          font-size: 14px;
        }
        th {
          background-color: var(--primary);
          color: #ffffff;
          font-weight: 700;
          text-transform: uppercase;
          font-size: 11px;
          letter-spacing: 0.7px;
          border: none;
          padding: 12px 14px;
          text-align: left;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        th:first-child { border-top-left-radius: 6px; }
        th:last-child { border-top-right-radius: 6px; }
        th.right-align, td.right-align { text-align: right; }
        th.left-align, td.left-align { text-align: left; }
        th.center-align, td.center-align { text-align: center; }
        td {
          border-bottom: 1px solid #e2e8f0;
          padding: 12px 14px;
          vertical-align: top;
        }
        tbody tr:nth-child(even) { background-color: #f8fafc; }
        tbody tr:hover { background-color: #f1f5f9; }
        textarea {
          width: 100%; border: none; font-family: inherit; font-size: 14px;
          resize: none; background: transparent; line-height: 1.4; color: var(--text-dark);
        }
        td input[type="text"] {
          width: 100%; border: none; font-family: inherit; font-size: 14px;
          text-align: right; background: transparent; color: var(--text-dark); min-width: 80px;
        }
        input:focus, textarea:focus, select:focus { outline: 2px solid var(--primary); outline-offset: -1px; border-radius: 4px; }
        .btn-container { max-width: 850px; margin: 24px auto 0 auto; display: flex; flex-wrap: wrap; gap: 12px; justify-content: flex-end; }
        .btn-primary-action {
          padding: 10px 24px; background: var(--primary); color: #fff; border: none;
          border-radius: 6px; cursor: pointer; font-size: 14px; font-weight: 600; transition: background 0.2s ease;
        }
        .btn-primary-action:hover { background: var(--primary-dark); }
        .add-icon {
          font-size: 13px; font-weight: 600; color: var(--primary); cursor: pointer;
          display: inline-flex; align-items: center; gap: 4px; padding: 5px 12px; border-radius: 99px; background: #eff6ff;
          transition: all 0.2s ease;
        }
        .add-icon:hover { background: #dbeafe; transform: translateY(-1px); }
        .remove-btn { color: #ef4444; cursor: pointer; font-size: 16px; user-select: none; transition: transform 0.2s ease; }
        .remove-btn:hover { color: #dc2626; transform: scale(1.15); }
        .summary-box {
          margin-top: 24px;
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 10px;
          font-size: 14px;
          color: var(--text-dark);
        }
        .summary-row { display: flex; justify-content: space-between; width: 270px; font-weight: 500; color: #475569; }
        .summary-row.total {
          font-size: 18px;
          border-top: none;
          padding: 10px 16px;
          margin-top: 6px;
          color: #ffffff;
          background-color: var(--primary);
          border-radius: 6px;
          width: 290px;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .invoice-footer-strip {
          background-color: var(--primary);
          color: #ffffff;
          border-radius: 8px;
          padding: 10px 20px;
          margin-top: 24px;
          display: flex;
          justify-content: space-around;
          align-items: center;
          font-size: 12px;
          font-weight: 600;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .payment-instructions {
          background-color: #f8fafc;
          padding: 18px 22px;
          border-radius: 10px;
          font-size: 13px;
          line-height: 1.6;
          color: #475569;
          border: 1px solid #cbd5e1;
          width: 100%;
          max-width: 420px;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .payment-instructions h3 { margin-top: 0; margin-bottom: 8px; color: #0f172a; font-size: 15px; font-weight: 700; }
        .bill-to-name { font-weight: 600; color: var(--text-dark); }
        .thank-you-note { margin-top: 32px; text-align: center; font-size: 18px; font-weight: 700; color: var(--primary); letter-spacing: 0.5px; font-style: italic; }
        .print-text { display: none; white-space: pre-wrap; font-family: inherit; font-size: 14px; line-height: 1.4; color: #000; }
        
        @media print {
          @page { size: auto; margin: 8mm 10mm; }
          .invoice-body {
            background: #fff !important;
            margin: 0;
            padding: 0 !important;
            zoom: 0.75 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .invoice-body .container, .invoice-body .container-fluid { max-width: 100% !important; width: 100% !important; padding: 0 !important; margin: 0 !important; }
          .invoice-body .py-5, .invoice-body .py-4 { padding-top: 0 !important; padding-bottom: 0 !important; }
          .invoice-body .invoice-page-container { padding: 0 !important; background: #fff !important; }
          .invoice-body .invoice-wrapper { width: 100% !important; background: #fff !important; margin: 0; padding: 0 !important; }
          .invoice-body .no-print { display: none !important; }
          .invoice-body .invoice-box {
            padding: 24px 30px !important;
            border: 1px solid #d1d5db !important;
            border-radius: 8px !important;
            box-shadow: none !important;
            max-width: 100% !important;
            width: 100% !important;
            margin: 0 !important;
            box-sizing: border-box !important;
          }
          .invoice-body .header-grid {
            margin-bottom: 16px !important;
            padding-bottom: 16px !important;
          }
          .invoice-body .logo-container img.logo {
            max-width: 320px !important;
          }
          .invoice-body .shaded-details-container {
            background-color: #f8fafc !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            padding: 16px 20px !important;
            border-radius: 6px !important;
            margin-bottom: 16px !important;
          }
          .invoice-body .actions-col { display: none !important; }
          .invoice-body input, .invoice-body textarea, .invoice-body select { border: none !important; background: transparent !important; padding: 0 !important; outline: none !important; appearance: none !important; -webkit-appearance: none !important; }
          .invoice-body textarea { resize: none; overflow: hidden; }
          .invoice-body input::placeholder, .invoice-body textarea::placeholder { color: transparent !important; }
          .invoice-body .bill-to-name { font-size: 15px !important; font-weight: 700 !important; color: #000 !important; }
          .invoice-body .bill-to-email { font-size: 13px !important; color: #333 !important; }
          .invoice-body .no-print-textarea { display: none !important; }
          .invoice-body .print-text { display: block !important; }
          .invoice-body .print-address-view { display: block !important; }
          
          .Toastify { display: none !important; }
          .invoice-body .data-page-header { display: none !important; }
          .invoice-body .period-container select { display: none !important; }
          .invoice-body .date-range { display: none !important; }
          .invoice-body .print-period-view { display: block !important; font-size: 13px; color: #000; text-align: left; }
          .invoice-body table { margin-top: 10px !important; }
          .invoice-body th { padding: 8px 10px !important; font-size: 11px !important; }
          .invoice-body td { padding: 8px 10px !important; font-size: 12px !important; }
          .invoice-body .section-title { margin-bottom: 6px !important; padding-bottom: 4px !important; }
          .invoice-body .summary-box { margin-top: 14px !important; }
          .invoice-body .payment-instructions { padding: 14px 18px !important; border-radius: 8px !important; }
          .invoice-body .thank-you-note { margin-top: 20px !important; font-size: 14px !important; }
          .invoice-body th { background-color: var(--primary) !important; color: #ffffff !important; }
          .invoice-body .invoice-footer-strip { background-color: var(--primary) !important; color: #ffffff !important; }
          .invoice-body .summary-row.total { background-color: var(--primary) !important; color: #ffffff !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
      `}</style>

      {viewMode === 'dashboard' && isAdmin ? (
        <div className="container-fluid px-0 no-print">
          {isAdmin && (
            <div className="row g-3 mb-4 pb-2">
              
              <div className="col-12 col-sm-6 col-lg-3">
                <div 
                  className="card h-100 p-4 border-0 rounded-4 shadow-sm bg-white position-relative overflow-hidden hover-lift stat-card"
                  onClick={() => { setStatusFilter('Paid'); setPageIndex(0); }}
                  style={{ cursor: 'pointer', outline: statusFilter === 'Paid' ? '2px solid #22c55e' : 'none', transition: 'all 0.2s ease' }}
                  title="Click to view Paid Invoices"
                >
                  <span className="stat-accent" style={{ background: 'linear-gradient(180deg,#22c55e,#15803d)', boxShadow: '2px 0 10px rgba(34,197,94,0.3)' }} />
                  <div className="d-flex align-items-center gap-3 position-relative z-1 mb-2">
                    <div className="rounded-4 d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 44, height: 44, background: 'linear-gradient(135deg,#dcfce7,#bbf7d0)' }}>
                      <FaMoneyBillWave size={18} style={{ color: '#15803d' }} />
                  </div>
                    <div>
                      <small className="text-muted fw-semibold text-uppercase d-block" style={{ fontSize: '10px', letterSpacing: '0.7px' }}>Paid Invoices</small>
                      <h2 className="mb-0 fw-bold text-dark" style={{ fontSize: '1.6rem', lineHeight: 1.1 }}>${paidTotal.toFixed(2)}</h2>
                </div>
              </div>
                  <div className="mt-2 position-relative z-1 text-muted small fw-medium">{paidCount} Paid <span className="mx-1">•</span> Tax: ${paidTaxTotal.toFixed(2)}</div>
                  </div>
                </div>

              <div className="col-12 col-sm-6 col-lg-3">
                <div 
                  className="card h-100 p-4 border-0 rounded-4 shadow-sm bg-white position-relative overflow-hidden hover-lift stat-card"
                  onClick={() => { setStatusFilter('Due'); setPageIndex(0); }}
                  style={{ cursor: 'pointer', outline: statusFilter === 'Due' ? '2px solid #f59e0b' : 'none', transition: 'all 0.2s ease' }}
                  title="Click to view Due Invoices"
                >
                  <span className="stat-accent" style={{ background: 'linear-gradient(180deg,#f59e0b,#d97706)', boxShadow: '2px 0 10px rgba(245,158,11,0.3)' }} />
                  <div className="d-flex align-items-center gap-3 position-relative z-1 mb-2">
                    <div className="rounded-4 d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 44, height: 44, background: 'linear-gradient(135deg,#fef3c7,#fde68a)' }}>
                      <FaClock size={18} style={{ color: '#d97706' }} />
              </div>
                    <div>
                      <small className="text-muted fw-semibold text-uppercase d-block" style={{ fontSize: '10px', letterSpacing: '0.7px' }}>Due Invoices</small>
                      <h2 className="mb-0 fw-bold text-dark" style={{ fontSize: '1.6rem', lineHeight: 1.1 }}>${dueTotal.toFixed(2)}</h2>
                  </div>
                </div>
                  <div className="mt-2 position-relative z-1 text-muted small fw-medium">{dueCount} Invoices Due</div>
              </div>
                  </div>

              <div className="col-12 col-sm-6 col-lg-3">
                <div className="card h-100 p-4 border-0 rounded-4 shadow-sm bg-white position-relative overflow-hidden hover-lift stat-card">
                  <span className="stat-accent" style={{ background: 'linear-gradient(180deg,#6366f1,#4338ca)', boxShadow: '2px 0 10px rgba(99,102,241,0.3)' }} />
                  <div className="d-flex align-items-center gap-3 position-relative z-1 mb-2">
                    <div className="rounded-4 d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 44, height: 44, background: 'linear-gradient(135deg,#ede9fe,#ddd6fe)' }}>
                      <FaUser size={18} style={{ color: '#4338ca' }} />
                </div>
                    <div>
                      <small className="text-muted fw-semibold text-uppercase d-block" style={{ fontSize: '10px', letterSpacing: '0.7px' }}>Personal Taxes</small>
                      <h2 className="mb-0 fw-bold text-dark" style={{ fontSize: '1.6rem', lineHeight: 1.1 }}>${personalTotal.toFixed(2)}</h2>
              </div>
                  </div>
                  <div className="mt-2 position-relative z-1 text-muted small fw-medium">All-time Personal Services</div>
                </div>
              </div>

              <div className="col-12 col-sm-6 col-lg-3">
                <div className="card h-100 p-4 border-0 rounded-4 shadow-sm bg-white position-relative overflow-hidden hover-lift stat-card">
                  <span className="stat-accent" style={{ background: 'linear-gradient(180deg,#0ea5e9,#0369a1)', boxShadow: '2px 0 10px rgba(14,165,233,0.3)' }} />
                  <div className="d-flex align-items-center gap-3 position-relative z-1 mb-2">
                    <div className="rounded-4 d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 44, height: 44, background: 'linear-gradient(135deg,#e0f2fe,#bae6fd)' }}>
                      <FaBuilding size={18} style={{ color: '#0369a1' }} />
                    </div>
                    <div>
                      <small className="text-muted fw-semibold text-uppercase d-block" style={{ fontSize: '10px', letterSpacing: '0.7px' }}>Corporate Taxes</small>
                      <h2 className="mb-0 fw-bold text-dark" style={{ fontSize: '1.6rem', lineHeight: 1.1 }}>${corporateTotal.toFixed(2)}</h2>
                    </div>
                  </div>
                  <div className="mt-2 position-relative z-1 text-muted small fw-medium">All-time Corporate Services</div>
                </div>
              </div>

            </div>
          )}

          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
            <h4 className="fw-bold mb-0 text-dark d-flex align-items-center gap-2">
              <FaFileInvoiceDollar className="text-primary" /> Invoice Manager 
              <span className="badge bg-light text-secondary border rounded-pill ms-2 fs-6 px-3">
                {filteredInvoices.length} {statusFilter !== 'All' ? statusFilter : ''} / {totalCount} Total
              </span>
            </h4>
            <div className="d-flex gap-2 flex-wrap align-items-center">
              <div className="position-relative" ref={filterRef}>
                <button
                  type="button"
                  className="btn btn-white rounded-pill border shadow-sm px-3 py-2 fw-semibold text-dark d-flex align-items-center gap-2 hover-lift"
                  onClick={() => setIsFilterOpen(prev => !prev)}
                  style={{
                    fontSize: '0.88rem',
                    backgroundColor: statusFilter === 'Due' ? '#fffbeb' : statusFilter === 'Paid' ? '#f0fdf4' : statusFilter === 'Cancelled' ? '#f8fafc' : '#ffffff',
                    borderColor: statusFilter === 'Due' ? '#fcd34d' : statusFilter === 'Paid' ? '#86efac' : '#e2e8f0'
                  }}
                  aria-expanded={isFilterOpen}
                  aria-label="Filter by invoice payment status"
                >
                  <FaFilter className="text-muted" size={11} />
                  <span>{statusFilter === 'Due' ? 'Due Only' : statusFilter === 'Paid' ? 'Paid Only' : statusFilter === 'Cancelled' ? 'Cancelled' : 'All Statuses'}</span>
                  <span className={`badge rounded-pill ms-1 ${statusFilter === 'Due' ? 'bg-warning-subtle text-warning-emphasis' : statusFilter === 'Paid' ? 'bg-success-subtle text-success' : statusFilter === 'Cancelled' ? 'bg-secondary-subtle text-secondary' : 'bg-primary-subtle text-primary'}`} style={{ fontSize: '10px' }}>
                    {statusFilter === 'Due' ? dueCount : statusFilter === 'Paid' ? paidCount : statusFilter === 'Cancelled' ? cancelledCount : totalCount}
                  </span>
                </button>

                {isFilterOpen && (
                  <div 
                    className="position-absolute start-0 mt-2 shadow-lg bg-white border rounded-4 py-2"
                    style={{ minWidth: '175px', top: '100%', zIndex: 1050, boxShadow: '0 10px 25px -5px rgba(0,0,0,0.12), 0 8px 10px -6px rgba(0,0,0,0.08)' }}
                  >
                    <button
                      type="button"
                      className={`dropdown-item px-3 py-2 fw-semibold d-flex align-items-center justify-content-between gap-2 border-0 bg-transparent ${statusFilter === 'Due' ? 'bg-warning-subtle text-dark font-bold' : 'text-dark'}`}
                      onClick={() => { setStatusFilter('Due'); setPageIndex(0); setIsFilterOpen(false); }}
                      style={{ fontSize: '0.86rem', cursor: 'pointer' }}
                    >
                      <span className="d-flex align-items-center gap-2">
                        <FaClock className="text-warning" size={13} /> Due Only
                      </span>
                      <span className="badge rounded-pill bg-warning-subtle text-warning-emphasis ms-auto" style={{ fontSize: '10px' }}>{dueCount}</span>
                    </button>

                    <button
                      type="button"
                      className={`dropdown-item px-3 py-2 fw-semibold d-flex align-items-center justify-content-between gap-2 border-0 bg-transparent ${statusFilter === 'Paid' ? 'bg-success-subtle text-dark font-bold' : 'text-dark'}`}
                      onClick={() => { setStatusFilter('Paid'); setPageIndex(0); setIsFilterOpen(false); }}
                      style={{ fontSize: '0.86rem', cursor: 'pointer' }}
                    >
                      <span className="d-flex align-items-center gap-2">
                        <FaCheckCircle className="text-success" size={13} /> Paid Only
                      </span>
                      <span className="badge rounded-pill bg-success-subtle text-success ms-auto" style={{ fontSize: '10px' }}>{paidCount}</span>
                    </button>

                    <button
                      type="button"
                      className={`dropdown-item px-3 py-2 fw-semibold d-flex align-items-center justify-content-between gap-2 border-0 bg-transparent ${statusFilter === 'Cancelled' ? 'bg-secondary-subtle text-dark font-bold' : 'text-dark'}`}
                      onClick={() => { setStatusFilter('Cancelled'); setPageIndex(0); setIsFilterOpen(false); }}
                      style={{ fontSize: '0.86rem', cursor: 'pointer' }}
                    >
                      <span className="d-flex align-items-center gap-2">
                        <FaBan className="text-secondary" size={13} /> Cancelled
                      </span>
                      <span className="badge rounded-pill bg-secondary-subtle text-secondary ms-auto" style={{ fontSize: '10px' }}>{cancelledCount}</span>
                    </button>

                    <div className="dropdown-divider my-1"></div>

                    <button
                      type="button"
                      className={`dropdown-item px-3 py-2 fw-semibold d-flex align-items-center justify-content-between gap-2 border-0 bg-transparent ${statusFilter === 'All' ? 'bg-primary-subtle text-dark font-bold' : 'text-dark'}`}
                      onClick={() => { setStatusFilter('All'); setPageIndex(0); setIsFilterOpen(false); }}
                      style={{ fontSize: '0.86rem', cursor: 'pointer' }}
                    >
                      <span className="d-flex align-items-center gap-2">
                        <FaList className="text-primary" size={13} /> All Statuses
                      </span>
                      <span className="badge rounded-pill bg-primary-subtle text-primary ms-auto" style={{ fontSize: '10px' }}>{totalCount}</span>
                    </button>
                  </div>
                )}
              </div>
              <div className="position-relative">
                <FaFilter className="text-muted position-absolute top-50 start-0 translate-middle-y ms-3" size={11} />
                <select
                  className="form-select rounded-pill border shadow-sm ps-5 py-2 fw-semibold text-dark"
                  value={yearFilter}
                  onChange={(e) => { setYearFilter(e.target.value); setPageIndex(0); }}
                  style={{ fontSize: '0.88rem', cursor: 'pointer' }}
                  aria-label="Filter by year"
                >
                  <option value="">All Years</option>
                  {yearOptions.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              <div className="position-relative">
                <FaSearch className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" size={14} />
              <input
                type="text"
                  className="form-control rounded-pill border shadow-sm ps-5"
                placeholder="Search invoices..."
                value={searchText}
                onChange={(e) => { setSearchText(e.target.value); setPageIndex(0); }}
                  style={{ width: '220px', fontSize: '0.9rem' }}
              />
              </div>
              <button 
                className="btn btn-light rounded-pill shadow-sm border fw-bold px-3 d-flex align-items-center gap-2 hover-lift"
                onClick={handleExportToExcel}
                disabled={filteredInvoices.length === 0}
              >
                <FaFileExcel className="text-success" /> <span className="d-none d-sm-inline">Export</span>
              </button>
              <button 
                className="btn btn-primary rounded-pill shadow-sm fw-bold px-4 d-flex align-items-center gap-2 hover-lift"
                onClick={() => {
                  setInvoiceId(null);
                  setInvoiceNum('');
                  setItems([{ id: Date.now(), itemName: "", itemDesc: "", period: "yearly", startDate: "", endDate: "", amount: "", hst: true }]);
                  setBillToName('');
                  setBillToEmail('');
                  setBillToAddress('');
                  setSourceRecord(null);
                  setViewMode('form');
                }}
              >
                <FaPlus size={12} /> <span className="d-none d-sm-inline">Create Invoice</span><span className="d-inline d-sm-none">New</span>
              </button>
            </div>
          </div>


          <div className="d-block d-md-none">
            {isLoading ? (
              <div className="d-flex flex-column gap-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="card border-0 rounded-4" style={{ overflow: 'hidden' }}>
                    <div className="card-body p-3">
                      <div className="d-flex gap-2 align-items-center mb-3">
                        <span className="skeleton-cell" style={{ width: 36, height: 36, borderRadius: '50%' }} />
                        <div>
                          <span className="skeleton-cell d-block mb-1" style={{ width: 120, height: 13 }} />
                          <span className="skeleton-cell d-block" style={{ width: 80, height: 11 }} />
                        </div>
                      </div>
                      <span className="skeleton-cell" style={{ width: '100%', height: 32, borderRadius: 8 }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : pageData.length === 0 ? (
              <div className="card border-0 rounded-4 shadow-sm bg-white">
                <div className="card-body text-center py-5">
                  <FaInbox className="text-muted mb-3 opacity-50" size={32} />
                  <h6 className="fw-bold text-dark mb-1">No matching invoices</h6>
                  <p className="mb-0 text-muted small">Try adjusting your search criteria.</p>
                </div>
              </div>
            ) : (
              pageData.map(inv => {
                let billTo = 'Unknown';
                try {
                  const parsed = JSON.parse(inv.bill_to_id);
                  billTo = parsed.name || inv.bill_to_id || '';
                } catch (e) { billTo = inv.bill_to_id; }
                
                let personalAmt = 0; let corporateAmt = 0;
                try {
                  const parsedItems = typeof inv.items === 'string' ? JSON.parse(inv.items) : inv.items;
                  if (parsedItems && Array.isArray(parsedItems)) {
                    parsedItems.forEach(item => {
                      const amt = parseFloat(item.amount?.toString().replace(/[^0-9.-]/g, '')) || 0;
                      const itemName = item.itemName || (item.description ? item.description.split(':')[0].trim() : '');
                      const nameLower = itemName.toLowerCase();
                      const personalNames = WORK_ITEMS.Personal.map(wi => wi.split(':')[0].trim().toLowerCase());
                      if (nameLower.includes('personal') || personalNames.includes(nameLower)) personalAmt += amt;
                      else corporateAmt += amt;
                    });
                  }
                } catch (e) {}

                const avatar = getAvatarColor(billTo);

                return (
                  <div key={inv.id} className="card mb-3 mobile-record-card shadow-sm border-0 rounded-4">
                    <div className="card-body p-3">
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div className="d-flex align-items-center gap-2">
                          <div className="name-avatar flex-shrink-0" style={{ backgroundColor: avatar.bg, color: avatar.text, width: 36, height: 36, fontSize: 12 }}>
                            {getInitials(billTo)}
                          </div>
                        <div>
                            <h6 className="mb-0 fw-bold text-dark text-truncate" style={{ maxWidth: '160px' }}>{billTo}</h6>
                            <div className="text-muted small mt-1">#{inv.invoice_num} • {inv.invoice_date}</div>
                          </div>
                        </div>
                        <div className="dropdown">
                          <button className="action-menu-trigger dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false" data-bs-boundary="window" aria-label="Actions" title="Actions">
                            <FaEllipsisV size={13} />
                          </button>
                          <ul className="dropdown-menu dropdown-menu-end shadow-sm border-0">
                            <li><button className="dropdown-item" onClick={() => handleViewInvoice(inv)}><FaEye className="me-2 text-primary" /> View Details</button></li>
                            <li><button className="dropdown-item" onClick={() => handleEmailInvoice(inv)}><FaEnvelope className="me-2 text-success" /> Email Invoice</button></li>
                          </ul>
                        </div>
                      </div>
                      
                      <div className="d-flex justify-content-between align-items-center p-2 bg-light rounded-3 mb-2">
                        <span className="text-muted small">Total Amount</span>
                        <span className="fw-bold text-dark fs-5">${(inv.total || 0).toFixed(2)}</span>
                      </div>
                      
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                          P: ${personalAmt.toFixed(0)} | C: ${corporateAmt.toFixed(0)}
                      </div>
                        <select 
                          className={`form-select form-select-sm fw-bold border rounded-pill shadow-sm status-pill ${inv.status === 'Paid' ? 'text-success border-success bg-success-subtle' : inv.status === 'Cancelled' ? 'text-secondary border-secondary bg-light' : 'text-warning border-warning bg-warning-subtle'}`}
                          value={inv.status || 'Due'}
                          onChange={(e) => handleStatusChange(inv, e.target.value)}
                          style={{ width: '100px', cursor: 'pointer' }}
                        >
                          <option value="Due" className="text-dark">Due</option>
                          <option value="Paid" className="text-dark">Paid</option>
                          <option value="Cancelled" className="text-dark">Cancelled</option>
                        </select>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="card shadow-sm border-0 d-none d-md-block rounded-4 bg-white">
            <div className="table-responsive rounded-4">
              <table className="table table-modern table-hover align-middle mb-0" style={{ tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '11%' }} />
                  <col style={{ width: '19%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '7%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '11%' }} />
                  <col style={{ width: '7%' }} />
                </colgroup>
                <thead className="bg-light text-muted small text-uppercase">
                  <tr>
                    <th className="px-3 py-3 border-0">Invoice #</th>
                    <th className="border-0">Date</th>
                    <th className="border-0">Bill To</th>
                    <th className="border-0">Personal</th>
                    <th className="border-0">Corporate</th>
                    <th className="border-0">Subtotal</th>
                    <th className="border-0">Tax</th>
                    <th className="border-0">Total</th>
                    <th className="border-0">Status</th>
                    <th className="border-0">Action</th>
                  </tr>
                </thead>
                <tbody className="border-top-0">
                  {isLoading ? (
                    <SkeletonRows count={8} />
                  ) : pageData.length === 0 ? (
                    <tr>
                      <td colSpan="10">
                        <div className="text-center py-5">
                          <FaInbox className="text-muted mb-3 opacity-50" size={36} />
                          <h5 className="fw-bold text-dark mb-1">No invoices found</h5>
                          <p className="mb-0 text-muted">No records match your current filters or you haven't created any invoices yet.</p>
                        </div>
                      </td>
                    </tr>
                  ) : pageData.map(inv => {
                    let billTo = 'Unknown';
                    try {
                      const parsed = JSON.parse(inv.bill_to_id);
                      billTo = parsed.name || inv.bill_to_id || '';
                    } catch (e) { billTo = inv.bill_to_id; }
                    
                    let personalAmt = 0; let corporateAmt = 0;
                    try {
                      const parsedItems = typeof inv.items === 'string' ? JSON.parse(inv.items) : inv.items;
                      if (parsedItems && Array.isArray(parsedItems)) {
                        parsedItems.forEach(item => {
                          const amt = parseFloat(item.amount?.toString().replace(/[^0-9.-]/g, '')) || 0;
                          const itemName = item.itemName || (item.description ? item.description.split(':')[0].trim() : '');
                          const nameLower = itemName.toLowerCase();
                          const personalNames = WORK_ITEMS.Personal.map(wi => wi.split(':')[0].trim().toLowerCase());
                          if (nameLower.includes('personal') || personalNames.includes(nameLower)) personalAmt += amt;
                          else corporateAmt += amt;
                        });
                      }
                    } catch (e) {}

                    return (
                      <tr key={inv.id}>
                        <td className="px-3 fw-bold text-dark">#{inv.invoice_num}</td>
                        <td className="text-nowrap text-muted small">{inv.invoice_date}</td>
                        <td className="fw-medium text-dark text-truncate">
                          <div className="d-flex align-items-center gap-2">
                            <ClientAvatar name={billTo} />
                            <span className="text-truncate" title={billTo}>{billTo}</span>
                          </div>
                        </td>
                        <td className="text-muted">${personalAmt.toFixed(2)}</td>
                        <td className="text-muted">${corporateAmt.toFixed(2)}</td>
                        <td className="text-muted">${(inv.subtotal || 0).toFixed(2)}</td>
                        <td className="text-muted">${(inv.tax || 0).toFixed(2)}</td>
                        <td className="fw-bold text-dark">${(inv.total || 0).toFixed(2)}</td>
                        <td>
                          <select 
                            className={`form-select form-select-sm fw-bold border rounded-pill shadow-sm status-pill ${inv.status === 'Paid' ? 'text-success border-success bg-success-subtle' : inv.status === 'Cancelled' ? 'text-secondary border-secondary bg-light' : 'text-warning border-warning bg-warning-subtle'}`}
                            value={inv.status || 'Due'}
                            onChange={(e) => handleStatusChange(inv, e.target.value)}
                            style={{ cursor: 'pointer' }}
                          >
                            <option value="Due" className="text-dark">Due</option>
                            <option value="Paid" className="text-dark">Paid</option>
                            <option value="Cancelled" className="text-dark">Cancelled</option>
                          </select>
                        </td>
                        <td>
                          <div className="dropdown">
                            <button className="action-menu-trigger dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false" data-bs-boundary="window" aria-label="Actions" title="Actions">
                              <FaEllipsisV size={13} />
                            </button>
                            <ul className="dropdown-menu dropdown-menu-end shadow-sm border-0">
                              <li>
                                <button className="dropdown-item py-2" onClick={() => handleViewInvoice(inv)}>
                                  <FaEye className="me-2 text-primary" /> View / Edit Details
                                </button>
                              </li>
                              <li>
                                <button className="dropdown-item py-2" onClick={() => handleEmailInvoice(inv)}>
                                  <FaEnvelope className="me-2 text-success" /> Email Invoice
                                </button>
                              </li>
                            </ul>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-center p-3 border-top gap-3">
              <div className="d-flex align-items-center gap-2">
                <span className="rounded-pill px-3 py-1 fw-semibold" style={{ background: '#f8fafc', color: '#475569', fontSize: '0.78rem', border: '1px solid #e2e8f0' }}>
                  {filteredInvoices.length === 0 ? '0' : `${pageIndex * pageSize + 1}–${Math.min(filteredInvoices.length, (pageIndex + 1) * pageSize)}`}
                  <span className="text-muted fw-normal"> of </span>
                  {filteredInvoices.length}
                </span>
                <span className="text-muted" style={{ fontSize: '0.78rem' }}>invoices</span>
                </div>
              
              <div className="d-flex align-items-center gap-1">
                <button className="page-pill" disabled={pageIndex === 0} onClick={() => setPageIndex(p => Math.max(p - 1, 0))} aria-label="Previous page">
                  <FaChevronLeft size={10} /> Prev
                  </button>
                {(() => {
                  if (totalPages <= 1) return null;
                  const _items = [];
                  const addPage = (i) => _items.push(
                    <button key={i} className={`page-pill${i === pageIndex ? ' active' : ''}`} onClick={() => setPageIndex(i)} aria-label={`Page ${i + 1}`} aria-current={i === pageIndex ? 'page' : undefined}>
                      {i + 1}
                    </button>
                  );
                  const addDots = (key) => _items.push(<span key={key} className="page-dots">···</span>);
                  
                  if (totalPages <= 7) {
                    for (let i = 0; i < totalPages; i++) addPage(i);
                  } else {
                    addPage(0);
                    if (pageIndex > 2) addDots('l');
                    for (let i = Math.max(1, pageIndex - 1); i <= Math.min(totalPages - 2, pageIndex + 1); i++) addPage(i);
                    if (pageIndex < totalPages - 3) addDots('r');
                    addPage(totalPages - 1);
                  }
                  return _items;
                })()}
                <button className="page-pill" disabled={pageIndex >= totalPages - 1} onClick={() => setPageIndex(p => Math.min(p + 1, totalPages - 1))} aria-label="Next page">
                  Next <FaChevronRight size={10} />
                  </button>
                </div>
              </div>
          </div>
        </div>
      ) : (
        <div className="invoice-wrapper">
          {isAdmin && (
            <div className="no-print mb-4" style={{ maxWidth: '850px', margin: '0 20px' }}>
               <button className="btn btn-light rounded-pill shadow-sm border fw-bold px-3 py-2 d-inline-flex align-items-center hover-lift" onClick={() => { setViewMode('dashboard'); }}>
                 <FaArrowLeft className="me-2" /> Back to Dashboard
               </button>
            </div>
          )}

        <div className="invoice-box">
          <div className="header-grid">
            <div className="header-company-info" style={{ textAlign: 'left' }}>
              <h1 className="invoice-title">INVOICE</h1>
              <div style={{ fontWeight: '700', fontSize: '18px', color: '#0f172a', marginTop: '8px' }}>Oriental Business Solutions Inc.</div>
              <div style={{ fontSize: '14px', color: '#475569', marginTop: '4px', lineHeight: '1.5' }}>2970 Drew Road Unit 201, Mississauga, ON L4T 0A6</div>
              <div style={{ fontSize: '14px', color: '#475569', marginTop: '2px', lineHeight: '1.5' }}>info@orientalbiz.ca | +1 (647) 855-6177 | www.orientalbiz.ca</div>
              <div style={{ fontSize: '13px', color: '#64748b', marginTop: '6px', lineHeight: '1.5' }}>BN 710351909RC0001 | GST/HST Registration No. 710351909RC0001</div>
            </div>
            <div className="logo-container">
              <img className="logo" src={Logo} alt="Oriental Business Solutions Logo" />
            </div>
          </div>

          <div className="shaded-details-container">
            <div className="bill-meta-grid">
              <div className="billing-details">
                <div className="section-subtitle">Bill to</div>
                <div className="billing-address-box">
                  <div className="no-print d-flex flex-column gap-1" style={{ maxWidth: '340px' }}>
                    <input 
                      type="text" 
                      className="form-control form-control-sm p-1 bill-to-name" 
                      placeholder="Client / Company Name" 
                      value={billToName} 
                      onChange={e => setBillToName(e.target.value)} 
                    />
                    <AddressAutocomplete 
                      className="form-control form-control-sm p-1" 
                      placeholder="Billing Address"
                      value={billToAddress}
                      onChange={(e) => {
                        setBillToAddress(typeof e === 'string' ? e : e.target.value);
                      }}
                      onPlaceSelected={(place) => {
                        if (place && place.formatted_address) {
                          setBillToAddress(place.formatted_address);
                        }
                      }}
                    />
                    <input 
                      type="email" 
                      className="form-control form-control-sm p-1 bill-to-email" 
                      placeholder="Email Address" 
                      value={billToEmail} 
                      onChange={e => setBillToEmail(e.target.value)} 
                    />
                  </div>
                  <div className="print-address-view">
                    <div style={{ fontSize: '15px', fontWeight: '700', color: '#000' }}>{billToName || 'Client / Company Name'}</div>
                    {billToAddress && <div style={{ whiteSpace: 'pre-wrap', fontSize: '13px', color: '#333', lineHeight: '1.4' }}>{billToAddress}</div>}
                    {billToEmail && <div style={{ fontSize: '13px', color: '#555' }}>{billToEmail}</div>}
                  </div>
                </div>
              </div>

              <div className="invoice-meta-section">
                <div className="section-subtitle mb-2">Invoice details</div>
                <div className="meta-grid">
                  <div className="meta-item">
                    <span className="meta-label">Invoice no.:</span>
                    <input type="text" className="meta-input fw-semibold" value={invoiceNum} onChange={e => setInvoiceNum(e.target.value)} placeholder="1716" />
                  </div>
                  <div className="meta-item">
                    <span className="meta-label">Terms:</span>
                    <span className="meta-value">Due on receipt</span>
                  </div>
                  <div className="meta-item">
                    <span className="meta-label">Invoice date:</span>
                    <input type="date" className="meta-input" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)} />
                  </div>
                  <div className="meta-item">
                    <span className="meta-label">Due date:</span>
                    <input type="date" className="meta-input" value={dueDate} onChange={e => setDueDate(e.target.value)} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div style={{ marginTop: "24px" }}>
            <div className="section-title">
              <span>Description of Services</span>
              <span className="add-icon no-print" onClick={handleAddRow}>＋ Add Item</span>
            </div>
            <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  <th style={{ width: "4%" }}>#</th>
                  <th style={{ width: "12%" }}>Date</th>
                  <th style={{ width: "22%" }}>Product or service</th>
                  <th style={{ width: "30%" }}>Description</th>
                  <th className="right-align" style={{ width: "6%" }}>Qty</th>
                  <th className="right-align" style={{ width: "9%" }}>Rate</th>
                  <th className="right-align" style={{ width: "9%" }}>Amount</th>
                  <th className="right-align" style={{ width: "8%" }}>Tax</th>
                  <th className="center-align no-print actions-col" style={{ width: "4%" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => {
                  const rateNum = parseFloat(item.amount?.toString().replace(/[^0-9.]/g, '')) || 0;
                  const qtyNum = parseFloat(item.qty) || 1;
                  const totalAmt = rateNum * qtyNum;
                  const itemDate = item.startDate || invoiceDate || new Date().toISOString().slice(0, 10);

                  return (
                    <tr key={item.id}>
                      <td className="fw-medium text-muted">{idx + 1}.</td>
                      <td className="text-nowrap" style={{ fontSize: '13px', color: '#475569' }}>
                        <div className="no-print">
                          <input 
                            type="date" 
                            value={item.startDate || invoiceDate} 
                            onChange={e => handleItemChange(item.id, 'startDate', e.target.value)} 
                            style={{ border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '12px', padding: '2px 4px' }}
                          />
                        </div>
                        <div className="print-text" style={{ fontSize: '13px' }}>{itemDate}</div>
                      </td>
                      <td>
                        <div className="no-print mb-2">
                          <select 
                            className="form-select form-select-sm"
                            onChange={(e) => {
                              if (e.target.value) {
                                const parts = e.target.value.split(':');
                                handleItemChange(item.id, 'itemName', parts[0].trim());
                                handleItemChange(item.id, 'itemDesc', parts.slice(1).join(':').trim());
                                e.target.value = '';
                              }
                            }}
                            value=""
                          >
                            <option value="" disabled>-- Quick fill service description --</option>
                            <optgroup label="Personal Tax Services">{WORK_ITEMS.Personal.map(wi => <option key={wi} value={wi}>{wi.split(':')[0]}</option>)}</optgroup>
                            <optgroup label="Corporate Tax Services">{WORK_ITEMS.Corporate.map(wi => <option key={wi} value={wi}>{wi.split(':')[0]}</option>)}</optgroup>
                          </select>
                        </div>
                        <div className="no-print d-flex flex-column gap-1">
                          <input 
                            type="text" 
                            className="fw-bold" 
                            value={item.itemName || ''} 
                            onChange={e => handleItemChange(item.id, 'itemName', e.target.value)} 
                            placeholder="Product or service name" 
                            style={{ width: '100%', border: 'none', background: 'transparent', outline: 'none', fontSize: '14px', color: 'var(--text-dark)', textAlign: 'left' }}
                          />
                        </div>
                        <div className="print-text">
                          <div style={{ fontWeight: '700', fontSize: '14px', color: '#000' }}>{item.itemName || 'Service'}</div>
                        </div>
                      </td>
                      <td>
                        <div className="no-print d-flex flex-column gap-1">
                          <textarea 
                            className="text-muted" 
                            rows="2" 
                            value={item.itemDesc || ''} 
                            onInput={handleTextareaInput} 
                            onChange={e => handleItemChange(item.id, 'itemDesc', e.target.value)} 
                            placeholder="Detailed description..." 
                            style={{ width: '100%', border: 'none', background: 'transparent', outline: 'none', fontSize: '13px', resize: 'none', overflow: 'hidden', lineHeight: '1.4' }}
                          />
                        </div>
                        <div className="print-text">
                          <div style={{ fontSize: '13px', color: '#475569', lineHeight: '1.4' }}>{item.itemDesc}</div>
                        </div>
                      </td>
                      <td className="right-align">
                        <div className="no-print">
                          <input 
                            type="number" 
                            min="1"
                            value={item.qty || 1} 
                            onChange={e => handleItemChange(item.id, 'qty', e.target.value)} 
                            style={{ width: '50px', textAlign: 'right', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '2px 4px' }}
                          />
                        </div>
                        <div className="print-text" style={{ textAlign: 'right' }}>{qtyNum}</div>
                      </td>
                      <td className="right-align">
                        <div className="no-print d-inline-flex align-items-center justify-content-end gap-1">
                          <span className="fw-semibold text-dark">$</span>
                          <input 
                            type="text" 
                            value={item.amount} 
                            onChange={e => {
                              let val = e.target.value;
                              if (val.startsWith('$')) val = val.substring(1);
                              handleItemChange(item.id, 'amount', val);
                            }} 
                            placeholder="0.00" 
                            style={{ width: '70px', textAlign: 'right' }} 
                          />
                        </div>
                        <div className="print-text" style={{ textAlign: 'right' }}>${rateNum.toFixed(2)}</div>
                      </td>
                      <td className="right-align fw-semibold text-dark">
                        ${totalAmt.toFixed(2)}
                      </td>
                      <td className="right-align">
                        <div className="no-print">
                          <label className="d-inline-flex align-items-center gap-1 cursor-pointer" style={{ fontSize: '12px' }}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={item.hst}
                              onChange={e => handleItemChange(item.id, 'hst', e.target.checked)}
                            />
                            <span>{item.hst ? '+HST' : 'Exempt'}</span>
                          </label>
                        </div>
                        <div className="print-text" style={{ textAlign: 'right', fontSize: '13px' }}>{item.hst ? '+HST' : 'Exempt'}</div>
                      </td>
                      <td className="center-align remove-btn no-print actions-col" onClick={() => handleRemoveRow(item.id)}>
                        <FaTrashAlt style={{ color: '#ef4444', cursor: 'pointer', fontSize: '15px' }} title="Remove row" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>

          <div style={{ display: 'flex', marginTop: '28px', justifyContent: 'space-between', flexWrap: 'wrap', gap: '24px' }}>
            <div style={{ flex: '1', minWidth: '280px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div className="payment-instructions">
                <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', marginBottom: '10px' }}>Payment Information</h3>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <span className="badge px-2 py-1" style={{ backgroundColor: 'var(--primary)', color: '#ffffff', fontSize: '10px', letterSpacing: '0.5px', borderRadius: '12px' }}>BANK</span>
                  <span style={{ fontSize: '13px', color: '#475569' }}>e-Transfer: <strong>info@orientalbiz.ca</strong></span>
                </div>
                <div style={{ fontSize: '13px', color: '#64748b' }}>
                  Account Name: <strong style={{ color: '#0f172a' }}>Oriental Business Solutions Inc.</strong>
                </div>
              </div>

              <div className="note-to-customer-box">
                <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: '4px' }}>NOTE TO CUSTOMER</div>
                <div style={{ fontSize: '13px', color: '#334155' }}>We appreciate your business and look forward to working with you again.</div>
              </div>


            </div>

            <div className="summary-box-card" style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '18px 22px', minWidth: '290px', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
              <div className="summary-row d-flex justify-content-between mb-2" style={{ fontSize: '13px', color: '#475569', fontWeight: '500' }}>
                <span>Subtotal</span><span>${subtotal.toFixed(2)}</span>
              </div>
              <div className="summary-row d-flex justify-content-between mb-2" style={{ fontSize: '13px', color: '#475569', fontWeight: '500' }}>
                <span>GST @ 0% on ${subtotal.toFixed(2)}</span><span>$0.00</span>
              </div>
              <div className="summary-row d-flex justify-content-between mb-3" style={{ fontSize: '13px', color: '#475569', fontWeight: '500' }}>
                <span>HST (ON) @ 13% on ${taxableSubtotal.toFixed(2)}</span><span>${tax.toFixed(2)}</span>
              </div>
              <div className="summary-row total d-flex justify-content-between align-items-center" style={{ backgroundColor: 'var(--primary)', color: '#ffffff', padding: '10px 16px', borderRadius: '6px', margin: 0 }}>
                <strong>Total</strong><strong style={{ fontSize: '20px' }}>${total.toFixed(2)}</strong>
              </div>
            </div>
          </div>
          
          <div className="thank-you-note">
            Thank you for your business!
          </div>

          <div className="invoice-footer-strip">
            <div className="d-flex align-items-center gap-2">
              <FaPhoneAlt /> <span>+1 (647) 855-6177</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <FaBuilding /> <span>www.orientalbiz.ca</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <FaEnvelope /> <span>info@orientalbiz.ca</span>
            </div>
          </div>
        </div>

        <div className="btn-container no-print mt-4 mb-5" style={{ maxWidth: '850px', margin: '0 auto' }}>
          <div className="card border-0 shadow-sm rounded-4 p-3 bg-white" style={{ border: '1px solid #e2e8f0' }}>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
              <div className="d-flex align-items-center gap-2">
                <div className="rounded-circle bg-primary-subtle p-2 d-flex align-items-center justify-content-center" style={{ width: 36, height: 36 }}>
                  <FaFileInvoiceDollar className="text-primary fs-6" />
                </div>
                <div>
                  <h6 className="mb-0 fw-bold text-dark" style={{ fontSize: '0.95rem' }}>Invoice Actions</h6>
                  <small className="text-muted" style={{ fontSize: '0.78rem' }}>Save, print or email this invoice</small>
                </div>
              </div>
              <div className="d-flex gap-2 flex-wrap ms-auto">
                <button
                  type="button"
                  disabled={isSaving}
                  className="btn btn-outline-secondary fw-bold px-4 py-2 rounded-pill shadow-sm hover-lift d-inline-flex align-items-center"
                  onClick={() => handleSaveInvoice(false, false)}
                  title="Save invoice"
                >
                  {isSaving ? <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" /> : <FaSave className="me-2" />}
                  Save
                </button>
                <button
                  type="button"
                  disabled={isSaving}
                  className="btn btn-outline-primary fw-bold px-4 py-2 rounded-pill shadow-sm hover-lift d-inline-flex align-items-center"
                  onClick={() => handleSaveInvoice(true, false)}
                  title="Save & Print invoice"
                >
                  {isSaving ? <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" /> : <FaPrint className="me-2" />}
                  Print
                </button>
                <button
                  type="button"
                  disabled={isSaving}
                  className="btn btn-primary fw-bold px-4 py-2 rounded-pill shadow-sm hover-lift d-inline-flex align-items-center"
                  onClick={() => handleSaveInvoice(true, true)}
                  style={{ backgroundColor: 'var(--primary)', borderColor: 'var(--primary)' }}
                  title="Save, Print & Email invoice"
                >
                  {isSaving ? <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" /> : <FaEnvelope className="me-2" />}
                  Email
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}
    </TaxPortalLayout>
  );
};

export default InvoicePage;